import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Form, FormElement } from "@progress/kendo-react-form";
import { Stepper } from "@progress/kendo-react-layout";
import AssetConfigurationComponent from './AssetConfigurationComponent';
import { useWindowSize } from 'usehooks-ts';
import { ReactQueryKeys } from '../../../Constants/ClientRoutingConstants';
import { useQuery } from 'react-query';
import assetsApiService from '../Utilities/AssetsApiService';
import templatesApiService from '../../Templates/Utilities/TemplatesApiService';
import LoadingSpinner from '../../Shared/Components/LoadingSpinner';
import ErrorCard from '../../Shared/Components/Cards/ErrorCard';
import { Groups, Endpoints, Gateways, gatewayFromSelection } from '../../../Models/Assets/AssetTemplateReadDto';
import AssetTemplateReadDto from '../../../Models/Assets/AssetTemplateReadDto';
import { enMessages } from '../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';
import AssetDetailsComponent from './AssetDetailsComponent';
import AssetGreasePumpComponent from './AssetGreasePumpComponent';
import { shouldShowConfigurationStep } from '../Utilities/AssetDetailsUtils';
import { isDeletedGroup, withoutDeletedGroups, withoutDeletedGroupEndpoints, onlyDeletedGroupEndpoints, applyGroupStatusToEndpoints } from '../Utilities/AssetConfigurationUtils';


interface Props {
  onClose: () => void;
  asset: any;
  onSave: (asset: any) => void;
  parentAssets: any;
}

const AssetDialog: React.FC<Props> = ({ onClose, asset, onSave, parentAssets }) => {
  const localizationService = useLocalization();
  const isEdit = !!asset;
  const { height } = useWindowSize();

  const selectedAssetQuery = useQuery<any>(
    [ReactQueryKeys.SelectedAssetQuery, asset?.id],
    async () => {
      return assetsApiService.getSelectedAsset(asset.id);
    },
    {
      enabled: isEdit && !!asset?.id,
    }
  );

  const [step, setStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  const fullTemplateQuery = useQuery(
    [ReactQueryKeys.SelectedTemplateQuery, selectedTemplate?.id],
    () => templatesApiService.getTemplateAsset(selectedTemplate!.id),
    // Also fetched when editing: only the template knows which groups were deleted
    { enabled: !!selectedTemplate?.id }
  );
  const [groups, setGroups] = useState<Groups[]>([]);
  const [endpoints, setEndpoints] = useState<Endpoints[]>([]);
  const [greasePumps, setGreasePumps] = useState<Groups[]>([]);
  const [templateConfig, setTemplateConfig] = useState<AssetTemplateReadDto | null>(null);
  const [assetDetails, setAssetDetails] = useState<any>(() => {
    if (isEdit && asset) {
      return {
        name: asset.name ?? '',
        manufacturer: asset.manufacturer ?? '',
        model: asset.model ?? '',
        parentId: asset.parentId ?? '',
        areaId: asset.areaId ?? '',
        machineryType: asset.machineryType ?? '',
        description: asset.description ?? '',
        photoUrl: asset.photoUrl ?? '',
        cuid: asset.cuid ?? '',
        namePlate: asset.namePlate ?? '',
        assetTemplate: asset.assetTemplate,
        greasePumpConnectivity: asset.greasePumpConnectivity ?? 'disabled'
      };
    }
    return {
      name: '',
      manufacturer: '',
      model: '',
      parentId: '',
      areaId: '',
      machineryType: 'Default',
      description: '',
      photoUrl: '',
      cuid: '',
      namePlate: '',
      assetTemplate: null,
      greasePumpConnectivity: 'disabled'
    };
  });
  const [gateways, setGateways] = useState<Gateways[]>([]);
  const [isGroupImageEditing, setIsGroupImageEditing] = useState(false);
  const [showConfigurationStep, setShowConfigurationStep] = useState(false);
  const [selectedGreasePumpId, setSelectedGreasePumpId] = useState<string | null>(null);
  const [selectedGateway, setSelectedGateway] = useState<{ id: string; serialNumber: string }>(() => {
    if (isEdit && asset?.assetTemplate?.gateways?.[0]) {
      return {
        id: asset.assetTemplate.gateways[0].id,
        serialNumber: asset.assetTemplate.gateways[0].serialNumber
      };
    }
    return { id: '', serialNumber: localizationService.toLanguageString('custom.notConfigured', enMessages.custom.notConfigured) };
  });

  const latestFormModel = React.useRef<any>(asset || {});
  const hasInitializedConfigRef = useRef<string | null>(null);
  const lastInitializedDataUpdatedAtRef = useRef<number>(0);
  // Groups deleted in the template are hidden from the UI but kept here so the save does not drop them
  const deletedGroupsRef = useRef<Groups[]>([]);
  const deletedGroupEndpointsRef = useRef<Endpoints[]>([]);

  // Use useEffect to set the initial state based on the query data.
  // Re-initialize when data changes (e.g. after template update + cache invalidation) so pins reflect latest positions.
  useEffect(() => {
    if (isEdit && selectedAssetQuery.data) {
      const assetId = selectedAssetQuery.data.id;
      const showConfig = shouldShowConfigurationStep(selectedAssetQuery.data.machineryType);
      setShowConfigurationStep(showConfig);
      if (!showConfig && step !== 0) {
        setStep(0);
      }
      // Set the template from the asset data
      if (selectedAssetQuery.data.assetTemplate) {
        setSelectedTemplate(selectedAssetQuery.data.assetTemplate);
        
        // Initialize when: first load for this asset, OR data was refetched (e.g. template was updated)
        const dataWasRefetched = selectedAssetQuery.dataUpdatedAt > lastInitializedDataUpdatedAtRef.current;
        const isFirstLoad = hasInitializedConfigRef.current !== assetId;
        if (isFirstLoad || dataWasRefetched) {
          hasInitializedConfigRef.current = assetId;
          lastInitializedDataUpdatedAtRef.current = selectedAssetQuery.dataUpdatedAt;
          const tpl = selectedAssetQuery.data.assetTemplate as AssetTemplateReadDto;
          const allGroups = tpl.groups || [];
          const allEndpoints = tpl.endpoints || [];
          // Keep the deleted ones aside: hidden from the UI, re-attached on save
          deletedGroupsRef.current = allGroups.filter(isDeletedGroup);
          deletedGroupEndpointsRef.current = onlyDeletedGroupEndpoints(allEndpoints, allGroups);
          if (dataWasRefetched) {
            setTemplateConfig({
              id: tpl.id,
              name: tpl.name,
              description: tpl.description || '',
              imageId: tpl.imageId || '',
              isGreasePumpConnectivityEnabled: tpl.isGreasePumpConnectivityEnabled ?? (tpl as any).hasGreasePumps ?? false,
              groups: withoutDeletedGroups(allGroups),
              endpoints: withoutDeletedGroupEndpoints(allEndpoints, allGroups),
              gateways: tpl.gateways || []
            });
          }
          if (selectedAssetQuery.data.assetTemplate.groups) {
            setGroups(withoutDeletedGroups(allGroups).map((g: Groups) => ({
              ...g,
              status: g.status ?? 'Active'
            })));
          }
          if (selectedAssetQuery.data.assetTemplate.endpoints) {
            setEndpoints(withoutDeletedGroupEndpoints(allEndpoints, allGroups).map((ep: Endpoints) => ({
              ...ep,
              status: ep.status ?? 'Active'
            })));
          }
          if (selectedAssetQuery.data.assetTemplate.greasePumps) {
            setGreasePumps(selectedAssetQuery.data.assetTemplate.greasePumps.map((p: any) => ({
              ...p,
              status: p.status ?? 'Active'
            })));
          }
          if (selectedAssetQuery.data.assetTemplate.gateways) {
            const mappedGateways = selectedAssetQuery.data.assetTemplate.gateways.map((g: Gateways) => ({
              ...g,
              status: g.status ?? 'Active',
              name: g.name || (g as any).serialNumber || `Gateway ${g.gatewayId || '1'}`
            }));
            setGateways(mappedGateways);
            const firstGw = mappedGateways[0];
            if (firstGw?.id) {
              setSelectedGateway({
                id: firstGw.id,
                serialNumber: (firstGw as any).serialNumber ?? firstGw.name ?? ''
              });
            }
          }
          const activeGreasePumps = (selectedAssetQuery.data.assetTemplate.greasePumps || []).filter(
            (p: any) => p.status === 'Active' || (p.status === undefined && p.isEnabled !== false)
          );
          setSelectedGreasePumpId(activeGreasePumps.length > 0 ? activeGreasePumps[0].id : null);
        }
      }
    }
  }, [isEdit, selectedAssetQuery.data, selectedAssetQuery.dataUpdatedAt, step]);


  // A group deleted in the template is only hidden when the asset never materialized its points (endpoints
  // without id): groups the asset already had keep their points, and must stay visible whatever their status
  useEffect(() => {
    if (!isEdit || !fullTemplateQuery.data) return;
    const templateDeletedIds = new Set(
      (fullTemplateQuery.data.groups || []).filter(isDeletedGroup).map((g: Groups) => g.id)
    );
    if (templateDeletedIds.size === 0) return;
    const assetEndpoints: Endpoints[] = selectedAssetQuery.data?.assetTemplate?.endpoints || [];
    const hasRealPoint = (groupId: string) => assetEndpoints.some(ep => ep.groupId === groupId && !!ep.id);
    const deletedGroupIds = new Set([...templateDeletedIds].filter(id => !hasRealPoint(id)));
    if (deletedGroupIds.size === 0) return;

    const groupsToHide = groups.filter(g => deletedGroupIds.has(g.id));
    if (groupsToHide.length > 0) {
      const alreadyKept = new Set(deletedGroupsRef.current.map(g => g.id));
      // Kept as they are: hiding them must not change their status in the payload
      deletedGroupsRef.current = [
        ...deletedGroupsRef.current,
        ...groupsToHide.filter(g => !alreadyKept.has(g.id))
      ];
      setGroups(groups.filter(g => !deletedGroupIds.has(g.id)));
    }

    const endpointsToHide = endpoints.filter(ep => deletedGroupIds.has(ep.groupId));
    if (endpointsToHide.length > 0) {
      // Endpoints copied from a deleted group come with an empty id, so key them by group + point
      const endpointKey = (ep: Endpoints) => `${ep.groupId}:${ep.pointId ?? ''}`;
      const alreadyKept = new Set(deletedGroupEndpointsRef.current.map(endpointKey));
      deletedGroupEndpointsRef.current = [
        ...deletedGroupEndpointsRef.current,
        ...endpointsToHide.filter(ep => !alreadyKept.has(endpointKey(ep)))
      ];
      setEndpoints(endpoints.filter(ep => !deletedGroupIds.has(ep.groupId)));
    }
  }, [isEdit, fullTemplateQuery.data, selectedAssetQuery.data, groups, endpoints]);

  // Track template ID changes to reset templateConfig when template changes
  const previousTemplateIdRef = useRef<string | null>(null);
  
  // Reset templateConfig when selectedTemplate changes (only in add mode)
  useEffect(() => {
    if (!isEdit && selectedTemplate?.id) {
      // If the template ID has changed, reset templateConfig
      if (previousTemplateIdRef.current !== selectedTemplate.id) {
        previousTemplateIdRef.current = selectedTemplate.id;
        setTemplateConfig(null);
      }
    }
  }, [selectedTemplate?.id, isEdit]);

  // Initialize templateConfig from full template when selected (add mode uses GET /web/v1/templates/assets list item; we need full template for config)
  useEffect(() => {
    if (!isEdit && selectedTemplate && fullTemplateQuery.data && selectedTemplate.id === fullTemplateQuery.data.id && !templateConfig) {
      const initialGateways = selectedGateway?.id ? [gatewayFromSelection(selectedGateway)] : (fullTemplateQuery.data.gateways || []);
      setGateways(initialGateways);
      setTemplateConfig({ ...fullTemplateQuery.data, gateways: initialGateways });
    }
    if (isEdit && selectedTemplate && !templateConfig) {
      // Use gateways from asset template (they have names from API), not gatewayFromSelection
      const initialGateways = (selectedTemplate as AssetTemplateReadDto).gateways || [];
      setGateways(initialGateways.map((g: Gateways) => ({ ...g, status: g.status ?? 'Active' })));
      setTemplateConfig({
        id: selectedTemplate.id,
        name: selectedTemplate.name,
        description: (selectedTemplate as AssetTemplateReadDto).description || '',
        imageId: (selectedTemplate as AssetTemplateReadDto).imageId || '',
        isGreasePumpConnectivityEnabled: (selectedTemplate as AssetTemplateReadDto).isGreasePumpConnectivityEnabled ?? (selectedTemplate as any).hasGreasePumps ?? false,
        groups: withoutDeletedGroups((selectedTemplate as AssetTemplateReadDto).groups || []),
        endpoints: withoutDeletedGroupEndpoints(
          (selectedTemplate as AssetTemplateReadDto).endpoints || [],
          (selectedTemplate as AssetTemplateReadDto).groups || []
        ),
        gateways: (selectedTemplate as AssetTemplateReadDto).gateways || []
      });
    }
  }, [isEdit, selectedTemplate, fullTemplateQuery.data, templateConfig, selectedGateway]);

  // Track reset state to prevent infinite loops
  const resetRef = useRef<string | null>(null);

  // Reset groups, endpoints, gateways, and grease pumps when dialog opens with new template (only in add mode)
  useEffect(() => {
    if (selectedTemplate?.id && !isEdit) {
      // Only reset once per template to prevent infinite loops, and only in add mode
      if (resetRef.current !== selectedTemplate.id) {
        resetRef.current = selectedTemplate.id;
        deletedGroupsRef.current = [];
        deletedGroupEndpointsRef.current = [];
        setGroups([]);
        setEndpoints([]);
        setGateways([]);
        setGreasePumps([]);
        setSelectedGreasePumpId(null);
      }
    }
  }, [selectedTemplate?.id, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps



  const handleTemplateChange = (updatedTemplate: AssetTemplateReadDto) => {
    setTemplateConfig(updatedTemplate);
  };

  const handleDetailsChange = (details: any) => {
    setAssetDetails(details);
    const showConfig = shouldShowConfigurationStep(details.machineryType);
    setShowConfigurationStep(showConfig);
    
    if (!showConfig && step !== 0) {
      setStep(0);
    }
    
    // If grease pump connectivity is disabled and we're on step 2, go back to step 1
    if (details.greasePumpConnectivity === 'disabled' && step === 2) {
      setStep(1);
    }
  };

  const handleSubmit = () => {
    let finalData;

    // Map groups - use status (Active/Inactive/Deleted); deleted ones are sent back untouched
    const allGroups = [...groups, ...deletedGroupsRef.current];
    const mappedGroups = allGroups.map(group => ({
      id: group.id,
      name: group.name,
      parentId: group.parentId || '',
      imageId: group.imageId || '',
      xPos: group.xPos || 0,
      yPos: group.yPos || 0,
      status: group.status ?? 'Active',
      attachmentImageIds: group.attachmentImageIds || []
    }));

    // Map endpoints - status follows the group's, so a disabled group never leaves an Active endpoint behind;
    // include pointId for backend uniqueness (same as template)
    const mappedEndpoints = applyGroupStatusToEndpoints(
      [...endpoints, ...deletedGroupEndpointsRef.current],
      allGroups
    ).map(endpoint => ({
      id: endpoint.id,
      groupId: endpoint.groupId,
      pointId: endpoint.pointId ?? '',
      name: endpoint.name || '',
      greasePumpId: endpoint.greasePumpId || '',
      endpointDeviceId: endpoint.endpointDeviceId || '',
      endpointDeviceSerialNumber: endpoint.endpointDeviceSerialNumber || '',
      requirementId: endpoint.requirementId || '',
      requirementName: endpoint.requirementName || '',
      xPos: endpoint.xPos || 0,
      yPos: endpoint.yPos || 0,
      status: endpoint.status ?? 'Active'
    }));

    // Map grease pumps - use status only (no isEnabled), same as groups/endpoints
    const rawGreasePumps = greasePumps?.length ? greasePumps : (templateConfig?.greasePumps || []);
    const mappedGreasePumps = rawGreasePumps.map((p: any) => ({
      id: p.id,
      name: p.name || '',
      deviceId: p.deviceId || '',
      serialNo: p.serialNo || '',
      channelNo: p.channelNo ?? 0,
      xPos: p.xPos ?? 0,
      yPos: p.yPos ?? 0,
      timingOffsetSeconds: p.timingOffsetSeconds ?? 0,
      status: p.status ?? 'Active',
      devices: p.devices ?? [],
      ...(p.mappedEndpoints != null && { mappedEndpoints: p.mappedEndpoints })
    }));

    const isGreasePumpConnectivityEnabled = assetDetails.greasePumpConnectivity === 'enabled';

    if (isEdit) {
      const { greasePumpConnectivity, ...assetDetailsWithoutConnectivity } = assetDetails;
      finalData = {
        ...asset,
        ...assetDetailsWithoutConnectivity,
        id: asset.id,
        assetTemplate: selectedTemplate ? {
          ...templateConfig,
          id: templateConfig?.id || selectedTemplate.id,
          isGreasePumpConnectivityEnabled,
          groups: mappedGroups,
          endpoints: mappedEndpoints,
          greasePumps: mappedGreasePumps,
          gateways: gateways.map(gw => ({ ...gw, status: gw.status ?? 'Active' }))
        } : null
      };
    } else {
      // Build the object for creating
      const { greasePumpConnectivity, ...assetDetailsWithoutConnectivity } = assetDetails;
      finalData = {
        ...assetDetailsWithoutConnectivity,
        assetTemplate: selectedTemplate ? {
          ...templateConfig,
          id: templateConfig?.id || selectedTemplate.id,
          isGreasePumpConnectivityEnabled,
          groups: mappedGroups,
          endpoints: mappedEndpoints,
          greasePumps: mappedGreasePumps,
          gateways: gateways.map(gw => ({ ...gw, status: gw.status ?? 'Active' }))
        } : null
      };
    }

    onSave(finalData);
    onClose();
  };

  const handlePrevious = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    setStep(step - 1);
  };

  function ensureObject(val: any) {
    return (val && typeof val === 'object' && !Array.isArray(val)) ? val : {};
  }

  const getDialogWidth = () => {
    if (step !== 0) {
      return "90vw";
    }
    return "min(max(90vw, 65vw), 900px)";
  }

  if(selectedAssetQuery.isLoading || selectedAssetQuery.isFetching) {
    return <LoadingSpinner />
  } else if (selectedAssetQuery.error) {
    return <ErrorCard />
  }
  return (
    <Dialog
      title={isEdit ? localizationService.toLanguageString('custom.editAsset', enMessages.custom.editAsset) + " " + asset.name : localizationService.toLanguageString('custom.addAsset', enMessages.custom.addAsset)}
      onClose={onClose}
      width={getDialogWidth()}
      height={height - 50}
    >
      <div className="h-full flex flex-col overscroll-contain">
        <Form
          onSubmit={handleSubmit}
          initialValues={asset || {}}
          render={(formRenderProps) => {
            latestFormModel.current = Object.keys(ensureObject(formRenderProps.modified)).length > 0
              ? { ...ensureObject(asset), ...ensureObject(formRenderProps.modified) }
              : ensureObject(asset);
            return (
              <FormElement className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto px-4 pt-3">
                  <div className={`mb-2 ${!showConfigurationStep ? 'single-step' : ''}`}>
                    <Stepper
                      value={step}
                      items={showConfigurationStep
                        ? assetDetails.greasePumpConnectivity === 'enabled'
                          ? [
                              { label: localizationService.toLanguageString('custom.details', enMessages.custom.details), isValid: true },
                              { label: localizationService.toLanguageString('custom.configuration', enMessages.custom.configuration), isValid: true },
                              { label: localizationService.toLanguageString('custom.greasePump', enMessages.custom.greasePump), isValid: true }
                            ]
                          : [
                              { label: localizationService.toLanguageString('custom.details', enMessages.custom.details), isValid: true },
                              { label: localizationService.toLanguageString('custom.configuration', enMessages.custom.configuration), isValid: true }
                            ]
                        : [
                            { label: localizationService.toLanguageString('custom.details', enMessages.custom.details), isValid: true }
                          ]}
                    />
                  </div>
                    {step === 0 && (
                      <AssetDetailsComponent
                        isEdit={isEdit}
                        assetData={selectedAssetQuery.data}
                        parentAssets={parentAssets}
                        selectedTemplate={selectedTemplate}
                        onTemplateSelect={setSelectedTemplate}
                        onDetailsChange={handleDetailsChange}
                        initialFormState={assetDetails}
                      />
                    )}

                    {step === 1 && showConfigurationStep && (
                      <AssetConfigurationComponent 
                        isEdit={isEdit}
                        selectedTemplate={isEdit ? selectedAssetQuery.data?.assetTemplate : (templateConfig || selectedTemplate)}
                        groups={groups}
                        setGroups={setGroups}
                        endpoints={endpoints}
                        setEndpoints={setEndpoints}
                        greasePumps={greasePumps}
                        setGreasePumps={setGreasePumps}
                        selectedGateway={selectedGateway}
                        onGatewayChange={setSelectedGateway}
                        gateways={gateways}
                        setGateways={setGateways}
                        onTemplateChange={handleTemplateChange}
                        onGroupImageEditStateChange={setIsGroupImageEditing}
                      />
                    )}

                    {step === 2 && showConfigurationStep && assetDetails.greasePumpConnectivity === 'enabled' && (
                      <AssetGreasePumpComponent 
                        isEdit={isEdit}
                        selectedTemplate={templateConfig || (isEdit ? selectedAssetQuery.data?.assetTemplate : selectedTemplate)}
                        groups={groups}
                        greasePumps={greasePumps}
                        setGreasePumps={setGreasePumps}
                        endpoints={endpoints}
                        setEndpoints={setEndpoints} 
                        selectedGateway={selectedGateway}
                        gateways={templateConfig?.gateways || (isEdit ? selectedAssetQuery.data?.assetTemplate?.gateways : [])}
                        selectedGreasePumpId={selectedGreasePumpId}
                        onGreasePumpSelect={setSelectedGreasePumpId}
                        onTemplateChange={handleTemplateChange}
                        onGreasePumpImageEditStateChange={setIsGroupImageEditing}
                        greasePumpConnectivity={assetDetails.greasePumpConnectivity}
                      />
                    )}
                  </div>

                  <div className="flex justify-end gap-2 px-4 pt-3 pb-4 border-t shrink-0">
                    <Button
                      themeColor="base"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                    {step > 0 && showConfigurationStep && (
                      <Button
                        themeColor="base"
                        onClick={(e) => handlePrevious(e)}
                        disabled={isGroupImageEditing}
                      >
                        {localizationService.toLanguageString('custom.previous', enMessages.custom.previous)}
                      </Button>
                    )}
                    {(step === 0 && showConfigurationStep) ? (
                      <Button
                        themeColor="primary"
                        disabled={!formRenderProps.valid}
                        onClick={(e) => {
                          e.preventDefault();
                          if (formRenderProps.valid) {
                            setStep(1);
                          }
                        }}
                      >
                        {localizationService.toLanguageString('custom.next', enMessages.custom.next)}
                      </Button>
                    ) : (step === 1 && showConfigurationStep && assetDetails.greasePumpConnectivity === 'enabled') ? (
                      <Button
                        themeColor="primary"
                        disabled={
                          !formRenderProps.valid ||
                          isGroupImageEditing
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          if (formRenderProps.valid && !isGroupImageEditing) {
                            setStep(2);
                          }
                        }}
                      >
                        {localizationService.toLanguageString('custom.next', enMessages.custom.next)}
                      </Button>
                    ) : (
                      <Button
                        themeColor="primary"
                        onClick={() => {
                          handleSubmit();
                        }}
                        disabled={
                          !formRenderProps.valid ||
                          isGroupImageEditing
                        }
                      >
                        {localizationService.toLanguageString('custom.save', enMessages.custom.save)}
                      </Button>
                    )}
                  </div>
                </FormElement>
              );
            }}
          />
      </div>
    </Dialog>
  );
};

export default AssetDialog; 